import React, { useState, useEffect } from 'react'
import { Plus, Trash2, Edit2, Save, X, RefreshCw, Settings } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  useCommonDeductions,
  useUpdateCommonDeductions,
  useAddCommonDeduction,
  useDeleteCommonDeduction,
  useResetCommonDeductions,
  CommonDeduction,
} from '@/hooks/useDeductions'

export const CommonDeductionsManager: React.FC = () => {
  const [editMode, setEditMode] = useState(false)
  const [editedDeductions, setEditedDeductions] = useState<CommonDeduction[]>([])
  const [newDeduction, setNewDeduction] = useState<CommonDeduction>({
    reason: '',
    points: 5,
    category: 'other'
  })
  const [showAddForm, setShowAddForm] = useState(false)

  // React Query hooks
  const { data: deductions = [], isLoading: loading } = useCommonDeductions()
  const updateMutation = useUpdateCommonDeductions()
  const addMutation = useAddCommonDeduction()
  const deleteMutation = useDeleteCommonDeduction()
  const resetMutation = useResetCommonDeductions()

  const categories = [
    { value: 'dishes', label: 'Kitchen' },
    { value: 'trash', label: 'Chores' },
    { value: 'supplies', label: 'Supplies' },
    { value: 'cleanliness', label: 'Cleaning' },
    { value: 'behavior', label: 'Behavior' },
    { value: 'screen', label: 'Screen Time' },
    { value: 'food', label: 'Food' },
    { value: 'other', label: 'Other' }
  ]

  // Sync editedDeductions when deductions change
  useEffect(() => {
    setEditedDeductions(deductions)
  }, [deductions])

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(editedDeductions)
      setEditMode(false)
      toast.success('Common deductions updated')
    } catch (error) {
      console.error('Failed to update deductions:', error)
      toast.error('Failed to update deductions')
    }
  }

  const handleAdd = async () => {
    if (!newDeduction.reason.trim()) {
      toast.error('Please enter a reason')
      return
    }

    try {
      await addMutation.mutateAsync(newDeduction)
      setNewDeduction({ reason: '', points: 5, category: 'other' })
      setShowAddForm(false)
      toast.success('Added new common deduction')
    } catch (error: any) {
      console.error('Failed to add deduction:', error)
      toast.error(error.response?.data?.detail || 'Failed to add deduction')
    }
  }

  const handleDelete = async (reason: string) => {
    if (!confirm(`Delete "${reason}"?`)) return

    try {
      await deleteMutation.mutateAsync(reason)
      toast.success('Deleted deduction')
    } catch (error) {
      console.error('Failed to delete deduction:', error)
      toast.error('Failed to delete deduction')
    }
  }

  const handleReset = async () => {
    if (!confirm('Reset to default common deductions? This will remove all custom deductions.')) return

    try {
      await resetMutation.mutateAsync()
      toast.success('Reset to defaults')
    } catch (error) {
      console.error('Failed to reset:', error)
      toast.error('Failed to reset')
    }
  }

  const updateDeduction = (index: number, field: keyof CommonDeduction, value: any) => {
    const updated = [...editedDeductions]
    updated[index] = { ...updated[index], [field]: value }
    setEditedDeductions(updated)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold flex items-center">
          <Settings className="w-5 h-5 mr-2" />
          Common Deductions
        </h2>
        <div className="flex items-center space-x-2">
          {!editMode ? (
            <>
              <button
                onClick={() => setShowAddForm(true)}
                className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </button>
              <button
                onClick={() => {
                  setEditMode(true)
                  setEditedDeductions([...deductions])
                }}
                className="px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center text-sm"
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </button>
              <button
                onClick={handleReset}
                className="px-3 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center text-sm"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Reset
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center text-sm"
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </button>
              <button
                onClick={() => {
                  setEditMode(false)
                  setEditedDeductions([...deductions])
                }}
                className="px-3 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center text-sm"
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold mb-3">Add New Common Deduction</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <input
                type="text"
                placeholder="Reason"
                value={newDeduction.reason}
                onChange={(e) => setNewDeduction({ ...newDeduction, reason: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <input
                type="number"
                placeholder="Points"
                value={newDeduction.points}
                onChange={(e) => setNewDeduction({ ...newDeduction, points: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border rounded-lg"
                min="1"
                max="50"
              />
            </div>
            <div>
              <select
                value={newDeduction.category}
                onChange={(e) => setNewDeduction({ ...newDeduction, category: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end space-x-2 mt-3">
            <button
              onClick={handleAdd}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
            >
              Add
            </button>
            <button
              onClick={() => {
                setShowAddForm(false)
                setNewDeduction({ reason: '', points: 5, category: 'other' })
              }}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Deductions List */}
      <div className="space-y-2">
        {(editMode ? editedDeductions : deductions).map((deduction, index) => (
          <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            {editMode ? (
              <>
                <input
                  type="text"
                  value={deduction.reason}
                  onChange={(e) => updateDeduction(index, 'reason', e.target.value)}
                  className="flex-1 px-2 py-1 border rounded"
                />
                <input
                  type="number"
                  value={deduction.points}
                  onChange={(e) => updateDeduction(index, 'points', parseInt(e.target.value) || 0)}
                  className="w-16 px-2 py-1 border rounded text-center"
                  min="1"
                  max="50"
                />
                <select
                  value={deduction.category}
                  onChange={(e) => updateDeduction(index, 'category', e.target.value)}
                  className="px-2 py-1 border rounded"
                >
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    const updated = editedDeductions.filter((_, i) => i !== index)
                    setEditedDeductions(updated)
                  }}
                  className="p-1 text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <div className="flex-1">
                  <span className="font-medium">{deduction.reason}</span>
                  <span className="ml-2 text-sm text-gray-500">({categories.find(c => c.value === deduction.category)?.label})</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                    -{deduction.points} pts
                  </span>
                  <button
                    onClick={() => handleDelete(deduction.reason)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {deductions.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No common deductions configured
        </div>
      )}
    </div>
  )
}